import React, { useState } from 'react';
import {
  Heart,
  Clock,
  Video,
  Activity,
  MessageSquare,
  FileText,
  Headphones,
  ChefHat,
  Users,
  Edit3,
  AlertCircle,
  History,
  Trash2
} from 'lucide-react';
import { UserFavorite } from '../../types/userFavorites';
import { ResourceHistory, ResourceSession } from '../../types/userActivity';
import { formatDuration } from '../../utils/favoritesUtils';
import { formatRelativeTime } from '../../utils/time';

interface FavoriteResourceCardProps {
  item: UserFavorite;
  onOpenLink: (url: string, title: string) => void;
  onToggleFlag: (id: string) => Promise<void>;
  togglingFlagId: string | null;
  onConfirmDelete: (id: string | null) => void;
  confirmingDeleteId: string | null;
  onDelete: (id: string) => Promise<void>;
  deletingItemId: string | null;
  historyItemId: string | null;
  onToggleHistory: (id: string | null) => void;
}

const getKindIcon = (kind: string) => {
  switch (kind) {
    case 'chat': return MessageSquare;
    case 'video': return Video;
    case 'audio': return Headphones;
    case 'recipe': return ChefHat;
    case 'article': return FileText;
    case 'meeting': return Users;
    case 'manual': return Edit3;
    case 'activity': return Activity;
    default: return Clock;
  }
};

const formatTimeRange = (session: ResourceSession) => {
  const start = new Date(session.openedAt);
  const end = new Date(session.closedAt);
  const fmt = (d: Date) => d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  return `${fmt(start)} – ${fmt(end)}`;
};

export const FavoriteResourceCard: React.FC<FavoriteResourceCardProps> = ({
  item,
  onOpenLink,
  onToggleFlag,
  togglingFlagId,
  onConfirmDelete,
  confirmingDeleteId,
  onDelete,
  deletingItemId,
  historyItemId,
  onToggleHistory,
}) => {
  const Icon = getKindIcon(item.kind);
  const linkUrl = item.payload?.url || item.payload?.metadata?.link;
  const isFavorited = item.payload?.metadata?.isFavorited === true;

  // Thumbnail is enriched by the backend from rm-content Cosmos DB (thumbnail_url or image.src)
  const imageUrl = item.payload.thumbnail;
  const [imgError, setImgError] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);
  const showImage = !!imageUrl && !imgError;

  // Branded gradient always shown as base layer, image loads on top
  const contentKind = (item.payload as any).contentKind || item.kind;

  // Kind-based gradient colors
  const gradientClass = contentKind === 'video' || contentKind === 'videos'
    ? 'bg-gradient-to-br from-rm-primary-700 via-rm-primary-700/80 to-rm-teal-600'
    : contentKind === 'audio' || contentKind === 'audios'
    ? 'bg-gradient-to-br from-purple-800 via-purple-700 to-rm-primary-700'
    : contentKind === 'recipe' || contentKind === 'recipes'
    ? 'bg-gradient-to-br from-amber-700 via-amber-600 to-rm-accent-500'
    : 'bg-gradient-to-br from-rm-primary-700 via-rm-primary-700/90 to-rm-teal-600';

  return (
    <div
      className={`relative rounded-card shadow-soft hover:shadow-card transition-shadow overflow-hidden min-h-[220px] ${gradientClass}`}
    >
      {/* Full-card background image with gradient overlay — lazy loads on top of gradient */}
      {showImage && (
        <>
          <img
            src={imageUrl}
            alt=""
            referrerPolicy="no-referrer"
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${imgLoaded ? 'opacity-100' : 'opacity-0'}`}
            onLoad={() => setImgLoaded(true)}
            onError={() => { console.warn('[FavCard] Image load failed:', imageUrl?.substring(0, 80)); setImgError(true); }}
          />
          {imgLoaded && (
            <div className="absolute inset-0 bg-white/20" />
          )}
        </>
      )}

      <div className="relative p-4 flex flex-col justify-end min-h-[220px]">
        <div className="flex items-start gap-3">
          <div
            className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-black/30 backdrop-blur-sm text-white ring-1 ring-white/20"
          >
            <Icon className="w-5 h-5" />
          </div>

          <div className="flex-1 min-w-0">
            {linkUrl ? (
              <button
                onClick={() => onOpenLink(linkUrl, item.payload.title)}
                className="text-left group"
              >
                <h3 className="text-base font-bold transition-colors line-clamp-2 text-white [text-shadow:_0_1px_8px_rgb(0_0_0_/_60%)] group-hover:text-white/90">
                  {item.payload.title}
                </h3>
              </button>
            ) : (
              <h3 className="text-base font-bold line-clamp-2 text-white [text-shadow:_0_1px_8px_rgb(0_0_0_/_60%)]">
                {item.payload.title}
              </h3>
            )}

            {item.payload.description && item.payload.description !== item.payload.title && (
              <p className="text-xs mt-1 line-clamp-2 text-white/80">
                {item.payload.description}
              </p>
            )}
          </div>
        </div>

        {/* Bottom row: stats left, actions right */}
        <div className="flex items-center justify-between mt-3 pt-2 border-t border-white/15">
          {/* Stats */}
          {(() => {
            const history = item.payload?.metadata?.history as ResourceHistory | undefined;
            return (
              <span className="text-xs text-white/70">
                {history && history.sessionCount > 0
                  ? formatRelativeTime(history.lastAccessedAt)
                  : `Added ${formatRelativeTime(item.createdAt)}`}
              </span>
            );
          })()}

          {/* Action buttons */}
          <div className="flex items-center gap-1">
            <button
              onClick={() => onToggleFlag(item.id)}
              disabled={togglingFlagId === item.id}
              className={`w-7 h-7 rounded-full flex items-center justify-center transition-colors ${
                isFavorited ? 'text-red-400 hover:text-red-300' : 'text-white/50 hover:text-red-400'
              } ${togglingFlagId === item.id ? 'opacity-50 cursor-not-allowed' : ''}`}
              title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
            >
              {togglingFlagId === item.id ? (
                <div className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
              ) : (
                <Heart className={`w-3.5 h-3.5 ${isFavorited ? 'fill-current' : ''}`} />
              )}
            </button>
            {/* History button */}
            {(() => {
              const hist = item.payload?.metadata?.history as ResourceHistory | undefined;
              if (!hist || hist.sessionCount === 0) return null;
              return (
                <button
                  onClick={() => onToggleHistory(historyItemId === item.id ? null : item.id)}
                  className={`w-7 h-7 rounded-full flex items-center justify-center transition-colors ${
                    historyItemId === item.id ? 'text-white bg-white/20' : 'text-white/50 hover:text-white'
                  }`}
                  title="View history"
                >
                  <History className="w-3.5 h-3.5" />
                </button>
              );
            })()}
            {!isFavorited && (
              <button
                onClick={() => onConfirmDelete(item.id)}
                disabled={deletingItemId === item.id}
                className="w-7 h-7 rounded-full flex items-center justify-center transition-colors text-white/50 hover:text-red-400"
                title="Delete permanently"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* Inline delete confirmation */}
        {confirmingDeleteId === item.id && (
          <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start gap-2 mb-2">
              <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-red-700">
                This item will be permanently deleted, including all timeline history. This action cannot be undone.
              </p>
            </div>
            <div className="flex items-center gap-2 justify-end">
              <button
                onClick={() => onConfirmDelete(null)}
                disabled={deletingItemId === item.id}
                className="px-3 py-1 text-xs font-medium text-rm-neutral-600 bg-white border border-rm-neutral-600/30 rounded-card hover:bg-rm-bg-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => onDelete(item.id)}
                disabled={deletingItemId === item.id}
                className="px-3 py-1 text-xs font-medium text-white bg-red-600 rounded-card hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {deletingItemId === item.id ? (
                  <span className="flex items-center gap-1">
                    <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Deleting…
                  </span>
                ) : (
                  'Delete'
                )}
              </button>
            </div>
          </div>
        )}

        {/* Inline History Panel */}
        {historyItemId === item.id && (() => {
          const hist = item.payload?.metadata?.history as ResourceHistory | undefined;
          if (!hist?.sessions?.length) return null;
          const sessions = [...hist.sessions].sort((a, b) => new Date(b.closedAt).getTime() - new Date(a.closedAt).getTime());
          return (
            <div className="mt-3 p-3 border rounded-lg bg-black/30 border-white/20">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <History className="w-3.5 h-3.5 text-white" />
                  <span className="text-xs font-semibold text-white">Viewing History</span>
                </div>
                <div className="flex items-center gap-3 text-xs text-white/70">
                  <span>{hist.sessionCount} session{hist.sessionCount !== 1 ? 's' : ''}</span>
                  <span className="inline-flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {formatDuration(hist.totalTimeMs)}
                  </span>
                </div>
              </div>
              <div className="space-y-1.5 max-h-40 overflow-y-auto">
                {sessions.map((session, idx) => {
                  const date = new Date(session.closedAt);
                  const dayStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                  const timeStr = formatTimeRange(session);
                  return (
                    <div key={idx} className="flex items-center justify-between text-xs py-1 px-2 rounded bg-black/30">
                      <span className="text-white/70">{dayStr}</span>
                      <span className="text-white/70">{timeStr}</span>
                      <span className="inline-flex items-center gap-1 font-medium text-white">
                        <Clock className="w-3 h-3" />
                        {formatDuration(session.durationMs)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
};
